import type { CollectionEntry, RarityTier } from '@gachakit/core/types/card';

const styles = `
  :host {
    display: block;
  }

  .wrap {
    display: grid;
    gap: 12px;
  }

  .search {
    position: relative;
  }

  .search::before {
    content: "";
    position: absolute;
    left: 14px;
    top: 50%;
    width: 13px;
    height: 13px;
    border: 2px solid rgba(247,239,218,0.58);
    border-radius: 50%;
    transform: translateY(-58%);
    pointer-events: none;
  }

  .search::after {
    content: "";
    position: absolute;
    left: 26px;
    top: 29px;
    width: 8px;
    height: 2px;
    background: rgba(247,239,218,0.58);
    transform: rotate(45deg);
    pointer-events: none;
  }

  input {
    width: 100%;
    min-height: 48px;
    padding: 0 14px 0 40px;
    border: 1px solid rgba(247, 239, 218, 0.14);
    border-radius: 8px;
    background:
      linear-gradient(180deg, rgba(247,239,218,0.08), rgba(247,239,218,0.035)),
      rgba(8, 9, 8, 0.5);
    color: #f7efda;
    outline: none;
  }

  input:focus {
    border-color: rgba(167, 245, 206, 0.48);
    box-shadow: 0 0 0 3px rgba(167, 245, 206, 0.12);
  }

  .filters {
    display: flex;
    gap: 7px;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
  }

  .filters::-webkit-scrollbar {
    display: none;
  }

  .filter {
    min-height: 36px;
    flex: 0 0 auto;
    border: 1px solid rgba(247, 239, 218, 0.12);
    border-radius: 999px;
    padding: 0 12px;
    color: rgba(247, 239, 218, 0.72);
    background: rgba(247, 239, 218, 0.055);
    font: 800 0.76rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: capitalize;
  }

  .filter[data-active='true'] {
    color: #07100c;
    background: linear-gradient(135deg, color-mix(in oklab, var(--rarity-color, #a7f5ce) 76%, #ffffff), var(--rarity-color, #a7f5ce));
    border-color: rgba(255,255,255,0.2);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .card {
    min-width: 0;
    display: grid;
    gap: 8px;
    padding: 8px;
    border: 1px solid color-mix(in oklab, var(--rarity-color) 42%, rgba(255,255,255,0.12));
    border-radius: 8px;
    background:
      linear-gradient(180deg, color-mix(in oklab, var(--rarity-color) 12%, transparent), rgba(247,239,218,0.04)),
      rgba(8, 9, 8, 0.48);
    color: #f7efda;
    text-align: left;
    box-shadow: 0 12px 26px rgba(0,0,0,0.22);
    transform: translateZ(0);
  }

  .card:active {
    transform: scale(0.985);
  }

  .thumb {
    position: relative;
    overflow: hidden;
    aspect-ratio: 0.82;
    border-radius: 7px;
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 22%, transparent), rgba(255,255,255,0.04)),
      rgba(255,255,255,0.05);
  }

  .thumb::before {
    content: attr(data-fallback);
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    color: rgba(247,239,218,0.68);
    font: 900 1.8rem/1 var(--font-display, "Sora", sans-serif);
  }

  .thumb::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, transparent 48%, rgba(0,0,0,0.5)),
      linear-gradient(125deg, transparent 0 38%, rgba(255,255,255,0.12) 50%, transparent 62%);
    pointer-events: none;
  }

  img {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    opacity: 0;
    transform: scale(1.03);
    transition: opacity 260ms ease, transform 420ms ease;
  }

  img[data-loaded='true'] {
    opacity: 1;
    transform: scale(1);
  }

  .rarity {
    position: absolute;
    z-index: 2;
    left: 7px;
    top: 7px;
    min-height: 24px;
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    border-radius: 999px;
    color: #07100c;
    background: linear-gradient(135deg, color-mix(in oklab, var(--rarity-color) 78%, #ffffff), var(--rarity-color));
    font: 900 0.62rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  .count {
    position: absolute;
    z-index: 2;
    right: 7px;
    bottom: 7px;
    min-height: 26px;
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    border-radius: 999px;
    background: rgba(8, 9, 8, 0.72);
    color: #f7efda;
    font: 900 0.72rem/1 var(--font-display, "Sora", sans-serif);
  }

  .name {
    margin: 0;
    color: #fff8e8;
    font: 900 0.88rem/1.08 var(--font-display, "Sora", sans-serif);
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
  }

  .meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: rgba(247,239,218,0.62);
    font-size: 0.72rem;
  }

  .meta span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .empty {
    grid-column: 1 / -1;
    display: grid;
    gap: 9px;
    justify-items: center;
    text-align: center;
    padding: 22px 14px;
    border-radius: 8px;
    border: 1px dashed rgba(247,239,218,0.18);
    background: rgba(247,239,218,0.045);
    color: rgba(247,239,218,0.68);
  }

  .empty-art {
    width: 64px;
    height: 84px;
    border-radius: 8px;
    border: 1px solid rgba(247,239,218,0.2);
    background:
      linear-gradient(145deg, rgba(167,245,206,0.22), transparent 55%),
      rgba(255,255,255,0.05);
    transform: rotate(-5deg);
  }

  .empty strong {
    color: #fff8e8;
    font: 900 1rem/1.1 var(--font-display, "Sora", sans-serif);
  }

  .empty p {
    margin: 0;
    font-size: 0.84rem;
    line-height: 1.35;
  }

  .empty button {
    min-height: 42px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    color: #07100c;
    background: linear-gradient(135deg, #a7f5ce, #f5c76b);
    font: 900 0.8rem/1 var(--font-display, "Sora", sans-serif);
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      transition: none;
    }
  }
`;

type RarityFilter = RarityTier | 'all';

const rarityFilters: RarityFilter[] = ['all', 'common', 'uncommon', 'rare', 'epic', 'legendary'];

export class CollectionElement extends HTMLElement {
  private readonly root: ShadowRoot;
  private entries: CollectionEntry[] = [];
  private query = '';
  private rarity: RarityFilter = 'all';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  set items(value: CollectionEntry[]) {
    this.entries = value;
    this.render();
  }

  private render(): void {
    const filtered = this.entries.filter((entry) => {
      const normalizedTags = entry.card.tags.map((tag) => tag.toLowerCase());
      const queryMatch =
        this.query.length === 0 ||
        entry.card.name.toLowerCase().includes(this.query) ||
        entry.card.subtitle?.toLowerCase().includes(this.query) ||
        normalizedTags.some((tag) => tag.includes(this.query));
      const rarityMatch = this.rarity === 'all' || entry.card.rarity === this.rarity;
      return queryMatch && rarityMatch;
    });

    const filters = rarityFilters
      .map(
        (rarity) => `
          <button class="filter" type="button" data-rarity="${rarity}" data-active="${this.rarity === rarity}">
            ${rarity === 'all' ? 'All' : rarity}
          </button>
        `
      )
      .join('');
    const cards =
      filtered.length > 0
        ? filtered.map((entry) => this.renderCard(entry)).join('')
        : this.renderEmpty();

    this.root.innerHTML = `
      <style>${styles}</style>
      <div class="wrap">
        <label class="search">
          <input type="search" placeholder="Search species or tags" value="${escapeAttribute(this.query)}" />
        </label>
        <div class="filters">${filters}</div>
        <div class="grid">${cards}</div>
      </div>
    `;

    this.root.querySelector('input')?.addEventListener('input', (event) => {
      this.query = (event.target as HTMLInputElement).value.trim().toLowerCase();
      this.render();
    });

    this.root.querySelectorAll<HTMLButtonElement>('.filter').forEach((button) => {
      button.addEventListener('click', () => {
        this.rarity = (button.dataset.rarity ?? 'all') as RarityFilter;
        this.render();
      });
    });

    this.root.querySelectorAll<HTMLButtonElement>('.card').forEach((button) => {
      button.addEventListener('click', () => {
        this.dispatchEvent(
          new CustomEvent('select', {
            bubbles: true,
            composed: true,
            detail: { id: button.dataset.id }
          })
        );
      });
    });

    this.root.querySelector('.empty button')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('open-pack', { bubbles: true, composed: true }));
    });

    this.root.querySelectorAll('img').forEach((image) => {
      image.addEventListener('load', () => image.setAttribute('data-loaded', 'true'), { once: true });
      image.addEventListener(
        'error',
        () => {
          image.remove();
        },
        { once: true }
      );
      if (image.complete && image.naturalWidth > 0) {
        image.setAttribute('data-loaded', 'true');
      }
    });
  }

  private renderCard(entry: CollectionEntry): string {
    const card = entry.card;
    const family = card.stats.find((stat) => stat.label.toLowerCase() === 'class')?.value ?? card.subtitle ?? 'Unknown';
    return `
      <button class="card" type="button" data-id="${escapeAttribute(card.id)}" data-rarity="${card.rarity}">
        <div class="thumb" data-fallback="${escapeAttribute(initialsFor(card.name))}">
          ${card.imageUrl ? `<img src="${escapeAttribute(card.imageUrl)}" alt="${escapeAttribute(card.name)}" loading="lazy" />` : ''}
          <span class="rarity">${escapeHtml(shortRarity(card.rarity))}</span>
          <span class="count">x${entry.count}</span>
        </div>
        <p class="name">${escapeHtml(card.name)}</p>
        <div class="meta">
          <span>${escapeHtml(family)}</span>
          <span>${escapeHtml(card.rarity)}</span>
        </div>
      </button>
    `;
  }

  private renderEmpty(): string {
    const filtered = this.entries.length > 0;
    return `
      <div class="empty">
        <span class="empty-art" aria-hidden="true"></span>
        <strong>${filtered ? 'No matching specimens' : 'Binder waiting for its first card'}</strong>
        <p>${filtered ? 'Adjust the search or rarity chips.' : 'Open a sealed field pack and file the first five cards.'}</p>
        <button type="button">${filtered ? 'Back to Vault' : 'Open Vault'}</button>
      </div>
    `;
  }
}

if (!customElements.get('gacha-collection')) {
  customElements.define('gacha-collection', CollectionElement);
}

function shortRarity(rarity: RarityTier): string {
  switch (rarity) {
    case 'legendary':
      return 'LEG';
    case 'epic':
      return 'EPIC';
    case 'rare':
      return 'RARE';
    case 'uncommon':
      return 'UNC';
    case 'common':
      return 'COM';
  }
}

function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((chunk) => chunk[0]?.toUpperCase() ?? '')
      .join('') || 'SG'
  );
}

function escapeHtml(value: string): string {
  return escapeAttribute(value);
}

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}
